import { useEffect, useMemo, useRef } from "react";
import { useHydrateAtoms } from "jotai/utils";
import type { SectionProps } from "../../../components/Questionnaire/Section";
import type { RuleSet } from "../../../components/Questionnaire/validation/form/types";
import { seeMoreCoversStateAtom } from "../state/seeMoreCoversStateAtom";
import { questionnaireReducerAtom } from "../state/questionnaireReducerAtom";
import { ACTIONS } from "../reducer/constants";
import { useQuestionnaireActions } from "./useQuestionnaireActions";
import { computeInitialQuestionnaireState } from "./computeInitialQuestionnaireState";

/**
 * Initialize questionnaire state without subscribing to changes.
 *
 * Hydrates atoms synchronously on first render to avoid SSR mismatches,
 * then replays actions via dispatch as a client-side fallback.
 * Subsequent prop changes are handled via useEffect.
 *
 * @see useQuestionnaire - initialize + subscribe to state
 */
export const useQuestionnaireInitialization = (
  sections?: SectionProps[],
  validations?: readonly RuleSet[],
  config: Record<string, unknown> = {},
) => {
  const actions = useQuestionnaireActions();
  const prevConfig = useRef(config);
  const prevSections = useRef(sections);
  const prevValidations = useRef(validations);

  // Compute initial state synchronously for hydration (once on mount).
  const initialState = useMemo(
    () => computeInitialQuestionnaireState(sections, validations, config),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Hydrate atoms synchronously so the first paint matches SSR output.
  useHydrateAtoms([
    [
      questionnaireReducerAtom,
      { type: ACTIONS.HYDRATE, payload: initialState.state },
    ],
    [seeMoreCoversStateAtom, initialState.seeMoreCovers],
  ]);

  // TODO: Test removing this mount effect — useHydrateAtoms above may be
  // sufficient, making this redundant. Remove if SSR hydration still works without it.
  // Replay actions on mount as a client-side fallback — useSyncExternalStore
  // may not see the hydrated value in the same render cycle.
  useEffect(() => {
    const hasConfig = Object.keys(config).length > 0;
    if (hasConfig) {
      actions.setConfig(config);
    }
    if (sections) {
      actions.setSections(sections);
    }
    const hasValidations = validations && validations.length > 0;
    if (hasValidations) {
      actions.setValidations(validations);
    }

    // Re-hide non-suggested personalised covers (setSections reprocesses from raw props).
    if (initialState.seeMoreCovers && initialState.hiddenCoverNames.size > 0) {
      actions.setHidden([...initialState.hiddenCoverNames]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle subsequent prop changes.
  useEffect(() => {
    const configChanged = config !== prevConfig.current;
    const sectionsChanged = sections !== prevSections.current;
    const validationsChanged = validations !== prevValidations.current;

    if (configChanged && Object.keys(config).length > 0) {
      actions.setConfig(config);
    }
    if (sectionsChanged && sections) {
      actions.setSections(sections);
    }
    if (validationsChanged && validations && validations.length > 0) {
      actions.setValidations(validations);
    }

    prevConfig.current = config;
    prevSections.current = sections;
    prevValidations.current = validations;
  }, [config, sections, validations, actions]);

  // Return pre-computed state as synchronous fallback for consumers.
  return initialState.state;
};
